<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { h } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import { useOperators } from 'dashboard/components-next/filter/operators.js';

import Button from 'next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';

const emit = defineEmits(['apply-filter', 'close', 'clear-filters']);

const filters = defineModel({
  type: Array,
  default: [],
});

const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');
const agents = useMapGetter('agents/getAgents');

const { operators, equalityOperators, presenceOperators } = useOperators();

const DEFAULT_FILTER = {
  attributeKey: 'assignee_id',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const dateFilterOperators = computed(() => [
  operators.value.equal_to,
  operators.value.is_greater_than,
  operators.value.is_less_than,
  operators.value.days_before,
]);

const dealsFilterTypes = computed(() => [
  {
    attributeKey: 'assignee_id',
    value: 'assignee_id',
    attributeName: 'Responsavel',
    label: 'Responsavel',
    inputType: 'multiSelect',
    options: (agents.value || []).map(agent => ({
      id: agent.id,
      name: agent.name,
    })),
    dataType: 'number',
    filterOperators: equalityOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'priority',
    value: 'priority',
    attributeName: 'Prioridade',
    label: 'Prioridade',
    inputType: 'multiSelect',
    options: [
      { id: 'urgent', name: 'Urgente' },
      { id: 'high', name: 'Alta' },
      { id: 'medium', name: 'Media' },
      { id: 'low', name: 'Baixa' },
      { id: 'none', name: 'Sem prioridade' },
    ],
    dataType: 'text',
    filterOperators: equalityOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'labels',
    value: 'labels',
    attributeName: 'Etiqueta',
    label: 'Etiqueta',
    inputType: 'multiSelect',
    options: (labels.value || []).map(label => ({
      id: label.title,
      name: label.title,
      icon: h('span', {
        class: 'rounded-full',
        style: {
          backgroundColor: label.color,
          height: '6px',
          width: '6px',
        },
      }),
    })),
    dataType: 'text',
    filterOperators: presenceOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'created_at',
    value: 'created_at',
    attributeName: 'Data de criacao',
    label: 'Data de criacao',
    inputType: 'date',
    dataType: 'text',
    filterOperators: dateFilterOperators.value,
    attributeModel: 'standard',
  },
  {
    attributeKey: 'close_date',
    value: 'close_date',
    attributeName: 'Data de fechamento',
    label: 'Data de fechamento',
    inputType: 'date',
    dataType: 'text',
    filterOperators: dateFilterOperators.value,
    attributeModel: 'standard',
  },
]);

const conditionsRef = ref([]);

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
  emit('clear-filters');
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
    return;
  }
  filters.value.splice(index, 1);
};

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const validateAndSubmit = () => {
  if (!isConditionsValid()) return;
  emit('apply-filter', filters.value);
};

onBeforeUnmount(() => emit('close'));

const outsideClickHandler = [() => emit('close'), { ignore: ['#toggleDealsFilterButton'] }];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl min-w-96 lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ t('CONTACTS_LAYOUT.FILTER.TITLE') }}
    </h3>

    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="index">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`deal-filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="dealsFilterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`deal-filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="dealsFilterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>

    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
